const APPROVED_REQUISITION_KEY = 'approvedRequisition';
const APPROVED_REQUISITION_ACTOR_KEY = 'approvedRequisitionActor';
const APPROVAL_ATTRIBUTE_REQUISITION = 'B2B Approval Requisition';
const APPROVAL_ATTRIBUTE_LOCATION = 'B2B Approval Location';
const APPROVAL_ATTRIBUTE_SIGNATURE = 'B2B Approval Signature';

function buildApprovalSignature(items) {
  return (items || [])
    .map((item) => `${String(item.variant_id ?? item.variantId)}:${Number(item.quantity || 0)}`)
    .sort()
    .join('|');
}

async function clearApprovalCartAttributes() {
  const attributes = {
    [APPROVAL_ATTRIBUTE_REQUISITION]: '',
    [APPROVAL_ATTRIBUTE_LOCATION]: '',
    [APPROVAL_ATTRIBUTE_SIGNATURE]: '',
  };

  await fetch(`${window.routes.root_url}cart/update.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ attributes }),
  });
}

async function reopenApprovedRequisition(requisitionId, actingCompanyContactId) {
  if (!requisitionId || !actingCompanyContactId) {
    return;
  }

  const response = await fetch(`/apps/b2b-order-approval/requisitions/${encodeURIComponent(requisitionId)}/reopen`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      actingCompanyContactId,
    }),
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.error || 'Could not restore this requisition to pending approvals.');
  }
}

function clearApprovedSessionState() {
  sessionStorage.removeItem(APPROVED_REQUISITION_KEY);
  sessionStorage.removeItem(APPROVED_REQUISITION_ACTOR_KEY);

  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.delete('approvedRequisition');
  window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
}

let releaseInFlight = false;

if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
    const approvedRequisitionId = sessionStorage.getItem(APPROVED_REQUISITION_KEY);
    const actingCompanyContactId = sessionStorage.getItem(APPROVED_REQUISITION_ACTOR_KEY);
    const cart = event?.cartData;

    if (!approvedRequisitionId || !cart || releaseInFlight) {
      return;
    }

    const cartApprovedRequisitionId = String(cart.attributes?.[APPROVAL_ATTRIBUTE_REQUISITION] || '').trim();
    const storedSignature = String(cart.attributes?.[APPROVAL_ATTRIBUTE_SIGNATURE] || '').trim();
    const currentSignature = buildApprovalSignature(cart.items || []);
    const cartStillMatchesApproval = (
      cart.item_count > 0
      && cartApprovedRequisitionId === approvedRequisitionId
      && storedSignature
      && storedSignature === currentSignature
    );

    if (cartStillMatchesApproval) {
      return;
    }

    releaseInFlight = true;

    try {
      await reopenApprovedRequisition(approvedRequisitionId, actingCompanyContactId);
    } finally {
      await clearApprovalCartAttributes().catch(() => {});
      clearApprovedSessionState();
      releaseInFlight = false;
    }
  });
}

class CartApprovalFlow extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.checkoutButton = this.querySelector('[data-approval-checkout]');
    this.submitButton = this.querySelector('[data-approval-submit]');
    this.message = this.querySelector('[data-approval-message]');
    this.error = this.querySelector('[data-approval-error]');

    this.approvalRequired = this.dataset.approvalRequired === 'true';
    this.companyId = this.dataset.companyId;
    this.locationId = this.dataset.locationId;
    this.customerId = this.dataset.customerId;
    this.customerName = this.dataset.customerName || '';
    this.companyName = this.dataset.companyName;
    this.locationName = this.dataset.locationName;
    this.pendingApprovalsPage = this.dataset.pendingApprovalsPage || '/pages/pending-approvals';
    this.noteSelector = this.dataset.noteSelector || '#Cart-note';
    this.checkoutFormId = this.dataset.checkoutFormId || 'cart';
    this.checkoutLabel = this.dataset.checkoutLabel || 'Checkout';
    if (!this.submitButton) return;

    this.submitButton.addEventListener('click', () => {
      this.submitForApproval();
    });

    void this.initializeState();
  }

  async initializeState() {
    const cart = await this.fetchCart().catch(() => null);
    const params = new URLSearchParams(window.location.search);
    const cartApprovedRequisitionId = this.getApprovedRequisitionIdFromCart(cart);

    this.approvedRequisitionId = (
      params.get('approvedRequisition')
      || sessionStorage.getItem(APPROVED_REQUISITION_KEY)
      || cartApprovedRequisitionId
      || ''
    );

    if (this.approvedRequisitionId) {
      const approvedCartValid = cart ? await this.validateApprovedCartState(cart) : false;
      if (approvedCartValid) {
        sessionStorage.setItem(APPROVED_REQUISITION_KEY, this.approvedRequisitionId);
        sessionStorage.setItem(APPROVED_REQUISITION_ACTOR_KEY, this.customerId || '');
        this.enableApprovedCheckoutMode();
        return;
      }
    }

    this.enablePendingApprovalMode();
  }

  enablePendingApprovalMode() {
    this.approvedRequisitionId = '';
    if (this.submitButton) {
      this.submitButton.hidden = !this.approvalRequired;
      this.submitButton.disabled = false;
      this.submitButton.style.display = this.approvalRequired ? '' : 'none';
    }

    if (!this.approvalRequired) {
      if (this.checkoutButton) {
        this.checkoutButton.hidden = false;
        this.checkoutButton.disabled = false;
        this.checkoutButton.style.display = '';
      }
      if (this.message) this.message.hidden = true;
      this.setError('');
      return;
    }

    if (this.checkoutButton) {
      this.checkoutButton.style.display = 'none';
      this.checkoutButton.hidden = true;
    }

    if (this.message) {
      this.message.textContent = 'This location requires approval before payment. Submitting this cart will place it in the pending approvals queue instead of sending you directly to checkout.';
      this.message.hidden = false;
    }

    this.setError('');
  }

  async fetchCart() {
    const response = await fetch(`${window.routes.root_url}cart.js`, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Could not load the current cart.');
    }

    return response.json();
  }

  getApprovedRequisitionIdFromCart(cart) {
    return String(cart?.attributes?.[APPROVAL_ATTRIBUTE_REQUISITION] || '').trim();
  }

  getApprovedSignatureFromCart(cart) {
    return String(cart?.attributes?.[APPROVAL_ATTRIBUTE_SIGNATURE] || '').trim();
  }

  async validateApprovedCartState(cart) {
    const approvedRequisitionId = this.approvedRequisitionId;
    const cartApprovedRequisitionId = this.getApprovedRequisitionIdFromCart(cart);
    const storedSignature = this.getApprovedSignatureFromCart(cart);
    const currentSignature = buildApprovalSignature(cart?.items || []);
    const cartStillMatchesApproval = (
      cart?.item_count > 0
      && cartApprovedRequisitionId
      && approvedRequisitionId
      && cartApprovedRequisitionId === approvedRequisitionId
      && storedSignature
      && storedSignature === currentSignature
    );

    if (cartStillMatchesApproval) {
      return true;
    }

    await this.releaseApprovedCart(approvedRequisitionId || cartApprovedRequisitionId);
    return false;
  }

  updateUrlSearchParam(key, value) {
    const nextUrl = new URL(window.location.href);

    if (value === null || value === undefined || value === '') {
      nextUrl.searchParams.delete(key);
    } else {
      nextUrl.searchParams.set(key, value);
    }

    window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
  }

  async clearApprovalCartAttributes() {
    await clearApprovalCartAttributes();
  }

  async releaseApprovedRequisition(requisitionId) {
    await reopenApprovedRequisition(requisitionId, this.customerId);
  }

  async releaseApprovedCart(requisitionId) {
    try {
      await this.releaseApprovedRequisition(requisitionId);
    } finally {
      await this.clearApprovalCartAttributes().catch(() => {});
      clearApprovedSessionState();
    }
  }

  enableApprovedCheckoutMode() {
    if (this.submitButton) {
      this.submitButton.hidden = true;
      this.submitButton.disabled = true;
      this.submitButton.style.display = 'none';
    }

    if (!this.checkoutButton) {
      this.checkoutButton = this.createCheckoutButton();
      const actions = this.querySelector('.cart-approval-flow__actions');
      if (actions) {
        actions.prepend(this.checkoutButton);
      }
    }

    if (this.checkoutButton) {
      this.checkoutButton.hidden = false;
      this.checkoutButton.disabled = false;
      this.checkoutButton.style.display = '';
    }

    if (this.message) {
      this.message.textContent = 'This requisition has been approved. Continue to checkout to complete payment.';
      this.message.hidden = false;
    }

    this.setError('');
  }

  createCheckoutButton() {
    const button = document.createElement('button');
    button.type = 'submit';
    button.name = 'checkout';
    button.form = this.checkoutFormId;
    button.className = 'btn btn__transition-text btn--checkout w-full';
    button.setAttribute('data-approval-checkout', '');
    button.innerHTML = `
      <div class="btn__texts">
        <span class="btn__top-text is-visible">${this.escapeHtml(this.checkoutLabel)}</span>
        <span class="btn__bottom-text is-hidden">${this.escapeHtml(this.checkoutLabel)}</span>
      </div>
    `;
    return button;
  }

  setError(message) {
    if (!this.error) return;
    this.error.innerHTML = message ? this.escapeHtml(message).replaceAll('\n', '<br>') : '';
    this.error.hidden = !message;
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  async parseJsonResponse(response) {
    const text = await response.text();
    const contentType = response.headers.get('content-type') || '';
    const trimmed = text.trim();

    if (trimmed.startsWith('<')) {
      throw new Error(
        `The app proxy returned HTML instead of JSON (HTTP ${response.status}). Reinstall the B2B Order Approval app on this store so the /apps/b2b-order-approval routes are active, then try again.`,
      );
    }

    if (contentType.includes('application/json')) {
      return text ? JSON.parse(text) : {};
    }

    return text ? JSON.parse(text) : {};
  }

  async submitForApproval() {
    if (!this.companyId || !this.locationId || !this.customerId) {
      this.setError('Missing company or location context for this cart.');
      return;
    }

    this.setError('');
    this.submitButton.disabled = true;

    try {
      const cart = await this.fetchCart();

      if (!cart.items || !cart.items.length) {
        throw new Error('Your cart is empty.');
      }

      const body = {
        shop: window.location.hostname,
        companyId: this.companyId,
        companyName: this.companyName || 'B2B Company',
        companyLocationId: this.locationId,
        locationName: this.locationName || 'Assigned location',
        requestingCustomerId: this.customerId,
        requestingCompanyContactId: this.customerId,
        requestingCustomerName: this.customerName || this.customerId,
        currency: cart.currency || 'USD',
        subtotal: String((cart.items_subtotal_price || 0) / 100),
        shipping: '0.00',
        tax: '0.00',
        total: String((cart.total_price || 0) / 100),
        notes: document.querySelector(this.noteSelector)?.value || '',
        items: cart.items.map((item) => ({
          variantId: String(item.variant_id),
          sku: item.sku || '',
          title: item.product_title || item.title,
          quantity: item.quantity,
          unitPrice: String((item.final_price || item.price || 0) / 100),
          lineTotal: String((item.final_line_price || 0) / 100),
          productData: JSON.stringify({
            productId: item.product_id,
            vendor: item.vendor,
            handle: item.handle,
          }),
        })),
      };

      const response = await fetch('/apps/b2b-order-approval/requisitions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });
      const payload = await this.parseJsonResponse(response);

      if (!response.ok) {
        throw new Error(payload.error || 'Could not submit the cart for approval.');
      }

      sessionStorage.removeItem(APPROVED_REQUISITION_KEY);
      sessionStorage.removeItem(APPROVED_REQUISITION_ACTOR_KEY);
      await this.clearCart();

      window.location.href = `${this.pendingApprovalsPage}?submitted=1`;
    } catch (error) {
      this.setError(error instanceof Error ? error.message : 'Could not submit the cart for approval.');
    } finally {
      this.submitButton.disabled = false;
    }
  }

  async clearCart() {
    const response = await fetch(`${window.routes.root_url}cart/clear.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Requisition was created, but the cart could not be cleared automatically.');
    }
  }
}

if (!customElements.get('cart-approval-flow')) {
  customElements.define('cart-approval-flow', CartApprovalFlow);
}
